/**
 * useUnitConversion - Composable for Imperial/Metric unit conversion
 *
 * Provides reactive unit conversion based on user's temperature unit preference.
 * Domain weather is stored in Celsius, km/h and mm. Only presentation values
 * change when the preference changes; these helpers never mutate or fetch weather.
 */

import { computed } from "vue";
import {
  convertWeatherTemperature,
  convertWeatherSpeed,
  convertWeatherPrecipitation,
  metersToFeet,
  milesToKilometers,
  roundToPlaces,
} from "rainowls-shared";
import {
  useSettingsStore,
  type TemperatureUnit,
} from "@/stores/settings.store";

/**
 * Unit labels for display
 */
export interface UnitLabels {
  temperature: string;
  temperatureShort: string;
  speed: string;
  precipitation: string;
  distance: string;
  elevation: string;
}

/**
 * Composable for unit conversion based on user preferences
 */
export function useUnitConversion(unit?: () => TemperatureUnit) {
  const settingsStore = unit ? null : useSettingsStore();
  const selectedUnit = unit ?? (() => settingsStore!.temperatureUnit);

  // Computed boolean for quick checks
  const isMetric = computed(() => selectedUnit() === "Metric");
  const isImperial = computed(() => selectedUnit() === "Imperial");

  // Unit labels based on current setting
  const labels = computed<UnitLabels>(() => {
    if (isMetric.value) {
      return {
        temperature: "°C",
        temperatureShort: "°",
        speed: "kmh",
        precipitation: "mm",
        distance: "km",
        elevation: "m",
      };
    }
    return {
      temperature: "°F",
      temperatureShort: "°",
      speed: "mph",
      precipitation: "in",
      distance: "mi",
      elevation: "ft",
    };
  });

  /** Keep missing-value sentinels and nonfinite API values out of every display formatter. */
  function isAvailable(value: number | null | undefined): value is number {
    return value !== null && value !== undefined && Number.isFinite(value)
      && value !== -999 && value !== -999.99;
  }

  // =========================================================================
  // Temperature Conversion
  // =========================================================================

  /**
   * Convert temperature from canonical Celsius to user's preferred unit
   * @param temperatureC - Temperature in Celsius
   * @param round - Whether to round to nearest integer (default: true)
   */
  function convertTemperature(
    temperatureC: number | null | undefined,
    round: boolean = true,
  ): number | null {
    if (!isAvailable(temperatureC))
      return null;

    const value = convertWeatherTemperature(temperatureC, "Metric", selectedUnit());
    return round ? Math.round(value) : roundToPlaces(value, 1);
  }

  /**
   * Format temperature with unit symbol
   */
  function formatTemperature(
    temperatureC: number | null | undefined,
    showUnit: boolean = true,
  ): string {
    const converted = convertTemperature(temperatureC);
    if (converted === null) return "--";
    return showUnit
      ? `${converted}${labels.value.temperature}`
      : `${converted}`;
  }

  // =========================================================================
  // Wind Speed Conversion
  // =========================================================================

  /**
   * Convert wind speed from canonical km/h to user's preferred unit
   * @param speedKmh - Speed in kilometres per hour
   */
  function convertSpeed(speedKmh: number | null | undefined): number | null {
    if (!isAvailable(speedKmh))
      return null;

    return Math.round(convertWeatherSpeed(speedKmh, "Metric", selectedUnit()));
  }

  /**
   * Format wind speed with unit
   */
  function formatSpeed(speedKmh: number | null | undefined): string {
    const converted = convertSpeed(speedKmh);
    if (converted === null) return "--";
    return `${converted} ${labels.value.speed}`;
  }

  // =========================================================================
  // Precipitation Conversion
  // =========================================================================

  /**
   * Convert precipitation from canonical millimetres to user's preferred unit
   * @param millimetres - Precipitation in millimetres
   * @param decimals - Number of decimal places (default: 2)
   */
  function convertPrecipitation(
    millimetres: number | null | undefined,
    decimals: number = 2,
  ): number | null {
    if (!isAvailable(millimetres))
      return null;

    return roundToPlaces(convertWeatherPrecipitation(millimetres, "Metric", selectedUnit()), decimals);
  }

  /**
   * Format precipitation with unit
   */
  function formatPrecipitation(
    millimetres: number | null | undefined,
    decimals: number = 2,
  ): string {
    const converted = convertPrecipitation(millimetres, decimals);
    if (converted === null) return "--";
    return `${converted.toFixed(decimals)} ${labels.value.precipitation}`;
  }

  // =========================================================================
  // Distance Conversion
  // =========================================================================

  /**
   * Convert distance from Imperial (miles) to user's preferred unit
   * @param miles - Distance in miles
   */
  function convertDistance(miles: number | null | undefined): number | null {
    if (!isAvailable(miles))
      return null;

    if (isMetric.value) {
      return roundToPlaces(milesToKilometers(miles), 1);
    }
    return roundToPlaces(miles, 1);
  }

  /**
   * Format distance with unit
   */
  function formatDistance(miles: number | null | undefined): string {
    const converted = convertDistance(miles);
    if (converted === null) return "--";
    return `${converted} ${labels.value.distance}`;
  }

  // =========================================================================
  // Elevation Conversion
  // =========================================================================

  /**
   * Convert elevation from canonical metres to user's preferred unit
   * @param metres - Elevation in metres
   */
  function convertElevation(metres: number | null | undefined): number | null {
    if (!isAvailable(metres))
      return null;

    return Math.round(isMetric.value ? metres : metersToFeet(metres));
  }

  /**
   * Format elevation with unit
   */
  function formatElevation(metres: number | null | undefined): string {
    const converted = convertElevation(metres);
    if (converted === null) return "--";
    return `${converted} ${labels.value.elevation}`;
  }

  return {
    // Reactive state
    isMetric,
    isImperial,
    labels,

    // Temperature
    convertTemperature,
    formatTemperature,

    // Speed
    convertSpeed,
    formatSpeed,

    // Precipitation
    convertPrecipitation,
    formatPrecipitation,

    // Distance
    convertDistance,
    formatDistance,

    // Elevation
    convertElevation,
    formatElevation,
  };
}
